// Shared (client + server safe) Supabase cookie/session configuration.
//
// The Supabase session used to live in `localStorage`, which iOS Safari's ITP
// wipes after ~7 days of no Safari interaction — installed Home Screen PWAs
// don't reset that timer, so residents kept getting logged out. The session now
// lives in a first-party cookie that is (re-)issued from the server through a
// real `Set-Cookie` response header, which ITP does not cap the same way.

export const SUPABASE_COOKIE_MAX_AGE = 60 * 60 * 24 * 400; // 400 days (browser cap)

export function isNewSupabaseApiKey(value: string): boolean {
  return value.startsWith('sb_publishable_') || value.startsWith('sb_secret_');
}

export function createSupabaseFetch(supabaseKey: string): typeof fetch {
  return (input, init) => {
    const headers = new Headers(
      typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined,
    );

    if (init?.headers) {
      new Headers(init.headers).forEach((value, key) => headers.set(key, value));
    }

    // New Supabase API keys are opaque strings, not bearer JWTs.
    if (isNewSupabaseApiKey(supabaseKey) && headers.get('Authorization') === `Bearer ${supabaseKey}`) {
      headers.delete('Authorization');
    }

    headers.set('apikey', supabaseKey);
    return fetch(input, { ...init, headers });
  };
}

/** Cookie attributes used for the auth cookie on both client and server. */
export function supabaseCookieOptions(secure: boolean) {
  return {
    path: '/',
    sameSite: 'lax' as const,
    secure,
    maxAge: SUPABASE_COOKIE_MAX_AGE,
  };
}

/** `sb-<project-ref>-auth-token` — the cookie/storage key Supabase derives from the URL. */
export function supabaseStorageKey(supabaseUrl: string): string {
  try {
    const host = new URL(supabaseUrl).hostname;
    return `sb-${host.split('.')[0]}-auth-token`;
  } catch {
    return 'sb-auth-token';
  }
}
